/**
 * @file useDropdownKeys.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-16
 * @modified 2026-09-16
 *
 * Keyboard control for every dropdown in the system.
 *
 * Until now not one of the four shared dropdowns — Dropdown, SearchableDropdown,
 * MultiSelectDropdown, ComboBox — had a single key handler between them. A form
 * could be filled from the keyboard right up to the moment it reached a
 * dropdown, and then the hand had to go to the mouse. In a line grid, where the
 * whole point is fast entry, that is the difference between typing an order and
 * clicking one.
 *
 * The rules, which are the same everywhere so that they can be learned once:
 *
 *   CLOSED, on the trigger
 *     ↓ / ↑ / Enter / Space   open the menu
 *     ← / →                   move to the previous / next field
 *     Tab / Shift+Tab         move to the next / previous field (the browser's)
 *     a printable character   open and start searching with it, where the
 *                             dropdown has a search box
 *
 *   OPEN
 *     ↓ / ↑                   move the cursor, clamped at both ends
 *     Home / End              first / last option
 *     Enter                   take the option under the cursor. A multi-select
 *                             STAYS OPEN — you are usually picking several, and
 *                             closing after each one is the behaviour that makes
 *                             people reach for the mouse. Anything else closes
 *                             and leaves focus on the trigger, so ↓ reopens.
 *     Tab                     take the option AND move to the next field, which
 *                             is the fast path through a form
 *     Escape                  close, change nothing, focus back on the trigger
 *
 * Two things worth knowing if you change this:
 *
 * 1. The cursor is NOT the selected value. `.dropdown-item.active` already
 *    means "this is the current value" and two effects scroll to it when a menu
 *    opens. The keyboard cursor is a separate class (`is-cursor`) so the two
 *    can differ — which they must, the moment you arrow away from the current
 *    value without choosing anything.
 *
 * 2. Tab is handled by moving focus ourselves rather than letting the browser
 *    do it. It has to be: accepting the option unmounts the menu (and with it
 *    the search input that had focus), so by the time the browser looked for
 *    "the next element after the focused one" there would be no focused element
 *    left and the tab order would restart from the top of the document.
 */

import { useCallback, useEffect, useRef } from 'react';

/** Everything the browser would let you Tab to, in document order. */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Move focus to the field before or after `from`.
 *
 * Skips anything inside `from`'s own dropdown — a menu's search box and option
 * rows are part of this control, not the next field — and anything invisible,
 * so a collapsed menu left in the DOM cannot swallow the focus.
 */
export function focusAdjacentField(from, direction) {
  if (!from) return false;
  const root = from.closest('.searchable-dropdown, .dropdown, .multi-select-dropdown') || from;
  const all = [...document.querySelectorAll(FOCUSABLE)].filter((el) => {
    if (el === from) return true;
    if (root.contains(el)) return false;
    // tabindex="-1" is focusable by script but deliberately out of the tab
    // order — the browser would skip it, so this does too.
    if (el.tabIndex < 0) return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0;
  });
  const i = all.indexOf(from);
  if (i === -1) return false;
  const next = all[i + (direction === 'next' ? 1 : -1)];
  if (!next) return false;
  next.focus();
  if (typeof next.select === 'function' && next.tagName === 'INPUT') next.select();
  return true;
}

/**
 * @param {object}   o
 * @param {boolean}  o.isOpen
 * @param {Function} o.setIsOpen
 * @param {Array}    o.navigable   options in the order they are DRAWN — for a
 *                                 grouped menu that is not the same array the
 *                                 filter produced, and arrowing down the wrong
 *                                 one jumps around the screen
 * @param {number}   o.cursor
 * @param {Function} o.setCursor
 * @param {Function} o.onChoose    (option, index) => void
 * @param {boolean}  o.multi       Enter keeps a multi-select open
 * @param {object}   o.triggerRef  focus returns here on close
 * @param {object}   o.listRef     scrolled so the cursor stays in view
 * @param {Function} [o.onType]    given the first character, for search boxes
 * @param {Function} [o.indexOfValue] where to put the cursor when opening
 * @param {boolean}  [o.spaceChooses] Space takes the option, like Enter. Only
 *                                 for a menu with no search box — in one that
 *                                 has one, Space is a character being typed.
 */
export default function useDropdownKeys({
  isOpen,
  setIsOpen,
  navigable = [],
  cursor,
  setCursor,
  onChoose,
  multi = false,
  triggerRef,
  listRef,
  onType,
  indexOfValue,
  spaceChooses = false,
}) {
  // Set when a key closed the menu, so focus goes back to the trigger once it
  // has re-rendered. Without it focus lands on <body> and the next keystroke
  // goes nowhere — the control looks like it has stopped responding.
  const refocusRef = useRef(false);

  useEffect(() => {
    if (isOpen || !refocusRef.current) return;
    refocusRef.current = false;
    triggerRef?.current?.focus?.();
  }, [isOpen, triggerRef]);

  // Keep the cursor on screen. `block: 'nearest'` scrolls only when it has to,
  // so holding ↓ walks the list instead of jumping it a page at a time.
  useEffect(() => {
    if (!isOpen || !listRef?.current) return;
    const el = listRef.current.querySelector('[data-cursor="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [isOpen, cursor, listRef]);

  const close = useCallback((refocus = true) => {
    refocusRef.current = refocus;
    setIsOpen(false);
  }, [setIsOpen]);

  const open = useCallback(() => {
    const at = typeof indexOfValue === 'function' ? indexOfValue() : 0;
    setCursor(at >= 0 ? at : 0);
    setIsOpen(true);
  }, [indexOfValue, setCursor, setIsOpen]);

  /** On the trigger, menu closed. */
  const onTriggerKeyDown = useCallback((e) => {
    if (isOpen) return;
    const { key } = e;
    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
      e.preventDefault();
      open();
      return;
    }
    if (key === 'ArrowRight' || key === 'ArrowLeft') {
      // Only from a button. In a ComboBox the trigger IS a text input, where
      // left and right belong to the caret.
      if (e.currentTarget.tagName === 'INPUT') return;
      e.preventDefault();
      focusAdjacentField(e.currentTarget, key === 'ArrowRight' ? 'next' : 'prev');
      return;
    }
    // Type-ahead: start searching without having to open the menu first.
    if (onType && key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      onType(key);
      setCursor(0);
      setIsOpen(true);
    }
  }, [isOpen, open, onType, setCursor, setIsOpen]);

  /** Anywhere inside an open menu — in practice the search input. */
  const onMenuKeyDown = useCallback((e) => {
    if (!isOpen) return;
    const { key } = e;
    const last = navigable.length - 1;

    if (key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(last, c + 1));
      return;
    }
    if (key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
      return;
    }
    if (key === 'Home') {
      e.preventDefault();
      setCursor(0);
      return;
    }
    if (key === 'End') {
      e.preventDefault();
      setCursor(last < 0 ? 0 : last);
      return;
    }
    if (key === 'Enter' || (spaceChooses && key === ' ')) {
      e.preventDefault();
      const option = navigable[cursor];
      // Nothing under the cursor — an empty search, or a ComboBox whose cursor
      // is deliberately parked at -1 so typed text is not replaced. Enter then
      // just closes the list and keeps what is there.
      if (!option) {
        close();
        return;
      }
      onChoose(option, cursor);
      // A multi-select is nearly always several picks, so it stays put.
      if (!multi) close();
      return;
    }
    if (key === 'Tab') {
      e.preventDefault();
      const option = navigable[cursor];
      // Tab takes what is under the cursor on the way past. On a multi-select
      // it does not, because the selection is already made by Enter and
      // toggling one more thing on the way out would be a surprise.
      if (option && !multi) onChoose(option, cursor);
      // Focus the trigger first so "the next field" is measured from this
      // control's place in the page, not from a search box about to vanish.
      refocusRef.current = false;
      setIsOpen(false);
      const trigger = triggerRef?.current;
      if (trigger) {
        trigger.focus();
        focusAdjacentField(trigger, e.shiftKey ? 'prev' : 'next');
      }
    }
  }, [isOpen, navigable, cursor, onChoose, multi, close, setCursor, setIsOpen, triggerRef, spaceChooses]);

  return { onTriggerKeyDown, onMenuKeyDown, closeFromKeyboard: close, openFromKeyboard: open };
}
